import axios from "axios";
import { LockIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { FiLoader } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiEndpoints } from "../../services/apiConfig";
import { toast } from "sonner";

const RazorpayPopup = ({
  isOpen,
  onClose,
  batchId,
  count,
  totalAmount,
  originalAmount,
  couponCode = null,
  userDetails, // New prop for user details
  onPaymentSuccess,
  remainingSessionId = "",
  remainingDownload = false,
}) => {
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [dateTime, setDateTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  // const RZP_KEY = import.meta.env.VITE_BACKEND_RAZORPAY_TEST_KEY;
  const RZP_KEY = import.meta.env.VITE_BACKEND_RAZORPAY_LIVE_KEY;

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDateTime(new Date());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement("script");
      script.src = apiEndpoints.razorpayCheckout;
      script.async = true;
      document.body.appendChild(script);
    };
    loadRazorpayScript();
  }, []);

  useEffect(() => {
    if (paymentStatus) {
      const timer = setTimeout(() => setPaymentStatus(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  function getCurrentDateTime() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const currentDateTime = getCurrentDateTime();

  const handleFailure = async (errorPayload) => {
    const token = localStorage.getItem("token");
    try {
      await axios.post(apiEndpoints.failedPayment, errorPayload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (reportError) {
      console.error("Error reporting failed payment:", reportError);
    }
  };

  const handleConfirmPayment = async (
    razorpayPaymentId,
    razorpayOrderId,
    jobId,
  ) => {
    const token = localStorage.getItem("token");
    const payload = {
      job_id: jobId,
      razorpay_payment_id: razorpayPaymentId,
      razorpay_order_id: razorpayOrderId,
    };

    try {
      const response = await axios.post(
        apiEndpoints.confirmEmailDownloadPayment,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        setPaymentStatus("success");

        if (onPaymentSuccess) {
          onPaymentSuccess(response.data);
        }

        setTimeout(() => {
          onClose();
          navigate("/my-downloads");
        }, 1000);
      } else {
        throw new Error(response.data.message || "Payment confirmation failed");
      }
    } catch (error) {
      console.error("Error confirming payment:", error);

      const errorPayload = {
        amount: totalAmount * 100,
        contact: userDetails?.mobile,
        email: userDetails?.email,
        razorpay_order_id: razorpayOrderId,
        date_time: currentDateTime,
        level: "3",
        problem: error.message,
      };

      await handleFailure(errorPayload);
      setPaymentStatus("failure");
      toast.error("Payment failed. Please try again.");
    }
  };

  const handlePaymentSuccess = async (response, jobId) => {
    await handleConfirmPayment(
      response.razorpay_payment_id,
      response.razorpay_order_id,
      jobId,
    );
  };

  const createOrder = async () => {
    const token = localStorage.getItem("token");
    const sessionId = localStorage.getItem("sessionId");

    const payload = {
      session_id: !remainingDownload ? sessionId : remainingSessionId,
      batch_id: batchId,
      count: count,
      currency: "INR",
    };

    if (couponCode) {
      payload.coupon_code = couponCode;
    }

    try {
      const response = await axios.post(
        apiEndpoints.createEmailDownloadOrder,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error creating order:", error);
      throw error;
    }
  };

  const handleRazorPayPayment = async () => {
    setIsCreatingOrder(true);

    // Use userDetails from props instead of form
    const name = `${userDetails?.firstName} ${userDetails?.lastName}`.trim();
    const phone = userDetails?.mobile;
    const email = userDetails?.email;

    // Validate user details
    if (!name || name.trim() === "") {
      setPaymentStatus("failure");
      setIsCreatingOrder(false);
      return;
    }

    if (!phone || !/^[0-9]{10}$/.test(phone)) {
      setPaymentStatus("failure");
      setIsCreatingOrder(false);
      return;
    }

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setPaymentStatus("failure");
      setIsCreatingOrder(false);
      return;
    }

    try {
      const orderData = await createOrder();
      if (!orderData.success) {
        throw new Error("Failed to create order");
      }

      const orderId = orderData?.data?.razorpay_order?.id;
      const jobId = orderData?.data?.job_id;
      const amountInPaise = orderData.data.amount || totalAmount * 100;

      const options = {
        key: RZP_KEY,
        amount: amountInPaise,
        currency: "INR",
        order_id: orderId,
        name: "Maxipitch",
        description: `Download ${count.toLocaleString("en-IN")} contacts data`,
        handler: async function (response) {
          await handlePaymentSuccess(response, jobId);
        },
        prefill: {
          name: name,
          email: email,
          contact: phone,
        },
        theme: {
          color: "#6366f1",
        },
        modal: {
          ondismiss: async function () {
            const errorPayload = {
              amount: amountInPaise,
              contact: phone,
              email: email,
              razorpay_order_id: orderId,
              date_time: currentDateTime,
              level: "2",
              problem: "User closed Razorpay modal",
            };

            await handleFailure(errorPayload);
            setPaymentStatus("failure");
            setIsCreatingOrder(false);
            toast.error("Payment failed. Please try again.");
          },
        },
      };

      const rzp1 = new window.Razorpay(options);
      rzp1.open();
      setIsCreatingOrder(false);
    } catch (error) {
      console.error("Error in payment process:", error);

      const errorPayload = {
        amount: totalAmount * 100,
        contact: phone,
        email: email,
        razorpay_order_id: "",
        date_time: currentDateTime,
        level: "1",
        problem: error.message,
      };

      await handleFailure(errorPayload);
      setPaymentStatus("failure");
      setIsCreatingOrder(false);
      toast.error("Payment failed. Please try again.");
    }
  };

  const handlePayment = async () => {
    setIsLoading(true);
    await handleRazorPayPayment();
    setIsLoading(false);
  };

  const closeModal = () => {
    setPaymentStatus(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Loader overlay */}
        {(isLoading || isCreatingOrder) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
            <FiLoader className="animate-spin text-indigo-600 text-4xl" />
          </div>
        )}

        {/* Header */}
        <div className="border-b border-gray-200 bg-white">
          <div className="flex items-start justify-between py-4 px-6">
            <div className="text-xl font-bold text-gray-900">
              Complete Your Purchase
            </div>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isLoading || isCreatingOrder}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Order Summary */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Order Summary
            </h2>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Contacts to download:</span>
                <span className="font-medium text-gray-900">
                  {count.toLocaleString("en-IN")} contacts
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium text-gray-900">
                  {formatCurrency(originalAmount)}
                </span>
              </div>

              {couponCode && originalAmount !== totalAmount && (
                <div className="flex justify-between items-center">
                  <span className="text-green-600">
                    Discount ({couponCode}):
                  </span>
                  <span className="text-green-600 font-medium">
                    -{formatCurrency(originalAmount - totalAmount)}
                  </span>
                </div>
              )}

              <div className="border-t border-gray-200 pt-3 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-900 font-semibold">
                    Total Amount:
                  </span>
                  <span className=" font-bold text-xl">
                    {formatCurrency(totalAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* User Details Summary */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Contact Details
            </h2>
            <div className="bg-blue-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-600 w-24">Name:</span>
                <span className=" text-gray-900">
                  {userDetails?.firstName} {userDetails?.lastName}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 w-24">Email:</span>
                <span className=" text-gray-900">
                  {userDetails?.email}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 w-24">Mobile:</span>
                <span className=" text-gray-900">
                  {userDetails?.mobile}
                </span>
              </div>
            </div>
          </div>

          {/* Security Info */}
          <div className="mb-6 space-y-2 text-xs sm:text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <LockIcon className="w-4 h-4 text-blue-600 " />
              <span>256-bit SSL Encrypted Secure Checkout</span>
            </div>
            <div className="flex items-center gap-2">
              <LockIcon className="w-4 h-4 text-blue-600" />
              <span>PCI-DSS Compliant Payment Gateway</span>
            </div>
            <div className="flex items-center gap-2">
              <LockIcon className="w-4 h-4 text-blue-600" />
              <span>Safe & Trusted Transactions</span>
            </div>
          </div>

          {/* Payment Button */}
          <button
            onClick={handlePayment}
            disabled={isLoading || isCreatingOrder}
            className={`w-full flex justify-center items-center gap-2 py-3 px-4 rounded-md shadow-md font-semibold transition-all
            ${
              isLoading || isCreatingOrder
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700 text-white"
            }`}
          >
            {isLoading || isCreatingOrder ? (
              <>
                <FiLoader className="animate-spin text-white text-lg" />
                <span>Processing...</span>
              </>
            ) : (
              <span>Pay {formatCurrency(totalAmount)}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RazorpayPopup;
